import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Mic, Eye, EyeOff, ArrowLeft, X, KeyRound } from "lucide-react";
import axios from "axios";
import { API } from "../utils/api";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Şifremi Unuttum state
  const [showReset, setShowReset] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetEmail, setResetEmail] = useState("");
  const [resetName, setResetName] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Hoş geldin!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Giriş yapılamadı");
    } finally {
      setLoading(false);
    }
  };

  const openResetModal = () => {
    setShowReset(true);
    setResetStep(1);
    setResetEmail(email || "");
    setResetName("");
    setResetPassword("");
    setResetConfirm("");
  };

  const closeResetModal = () => {
    setShowReset(false);
    setResetStep(1);
  };

  const handleResetSubmit = async () => {
    if (resetStep === 1) {
      if (!resetEmail) {
        toast.error("Lütfen e-posta adresinizi girin");
        return;
      }
      setResetStep(2);
      return;
    }

    if (!resetName.trim()) {
      toast.error("Lütfen kayıt sırasında girdiğiniz ismi yazın");
      return;
    }
    if (resetPassword.length < 6) {
      toast.error("Şifre en az 6 karakter olmalı");
      return;
    }
    if (resetPassword !== resetConfirm) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }

    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/verify-reset`, {
        email: resetEmail,
        name: resetName,
        new_password: resetPassword,
      });
      toast.success("Şifreniz güncellendi! Giriş yapabilirsiniz.");
      setEmail(resetEmail);
      setPassword("");
      closeResetModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Şifre sıfırlama başarısız");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F9FF] flex flex-col">
      <div className="p-4">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-[#118AB2] font-semibold hover:text-[#073B4C] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Ana Sayfa
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[#4CC9F0] rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Mic className="w-10 h-10 text-[#073B4C]" />
            </div>
            <h1 className="text-3xl font-black text-[#073B4C] font-['Nunito']">
              Tekrar Hoş Geldin!
            </h1>
            <p className="text-[#118AB2] mt-2">Hesabına giriş yap</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="space-y-6">
              <div>
                <label className="block text-[#073B4C] font-bold mb-2">E-posta</label>
                <input
                  type="email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-default"
                  placeholder="ornek@email.com"
                />
              </div>

              <div>
                <label className="block text-[#073B4C] font-bold mb-2">Şifre</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-default pr-12"
                    placeholder="--------"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8D99AE] hover:text-[#073B4C]"
                  >
                    {showPassword ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
                  </button>
                </div>

                <button
                  type="button"
                  data-testid="forgot-password-btn"
                  onClick={openResetModal}
                  className="mt-2 text-sm text-[#118AB2] hover:text-[#073B4C] font-medium transition-colors"
                >
                  Şifremi Unuttum
                </button>
              </div>

              <button
                type="submit"
                data-testid="login-submit"
                disabled={loading}
                className="btn-primary w-full disabled:opacity-50"
              >
                {loading ? "Giriş Yapılıyor..." : "Giriş Yap"}
              </button>
            </div>
          </form>

          <p className="text-center mt-6 text-[#118AB2]">
            Hesabın yok mu?{" "}
            <Link
              to="/register"
              data-testid="goto-register"
              className="text-[#4CC9F0] font-bold hover:underline"
            >
              Kayıt Ol
            </Link>
          </p>
        </div>
      </div>

      {/* Şifremi Unuttum Modal */}
      {showReset && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div
            data-testid="reset-password-modal"
            className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl relative"
          >
            <button
              data-testid="close-reset-modal"
              onClick={closeResetModal}
              className="absolute top-4 right-4 text-[#8D99AE] hover:text-[#073B4C] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[#FFD166] rounded-2xl flex items-center justify-center mx-auto mb-3">
                <KeyRound className="w-7 h-7 text-[#073B4C]" />
              </div>
              <h2 className="text-xl font-black text-[#073B4C] font-['Nunito']">
                Şifre Sıfırlama
              </h2>
              <p className="text-sm text-[#8D99AE] mt-1">
                {resetStep === 1
                  ? "Kayıtlı e-posta adresinizi girin"
                  : "Kayıt sırasında girdiğiniz ismi ve yeni şifrenizi yazın"}
              </p>
            </div>

            {resetStep === 1 ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-[#073B4C] font-bold mb-2 text-sm">E-posta</label>
                  <input
                    type="email"
                    data-testid="reset-email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="input-default"
                    placeholder="ornek@email.com"
                    autoFocus
                  />
                </div>
                <button
                  data-testid="reset-next-btn"
                  onClick={handleResetSubmit}
                  className="btn-primary w-full"
                >
                  Devam Et
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-[#F0F9FF] rounded-xl p-3 text-sm text-[#118AB2]">
                  <span className="font-semibold">{resetEmail}</span> için şifre sıfırlama
                </div>

                <div>
                  <label className="block text-[#073B4C] font-bold mb-2 text-sm">
                    Kayıtlı İsim
                  </label>
                  <input
                    type="text"
                    data-testid="reset-name"
                    value={resetName}
                    onChange={(e) => setResetName(e.target.value)}
                    className="input-default"
                    placeholder="Kayıt sırasında girdiğiniz isim"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-[#073B4C] font-bold mb-2 text-sm">
                    Yeni Şifre
                  </label>
                  <input
                    type="password"
                    data-testid="reset-new-password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="input-default"
                    placeholder="En az 6 karakter"
                  />
                </div>

                <div>
                  <label className="block text-[#073B4C] font-bold mb-2 text-sm">
                    Yeni Şifre Tekrar
                  </label>
                  <input
                    type="password"
                    data-testid="reset-confirm-password"
                    value={resetConfirm}
                    onChange={(e) => setResetConfirm(e.target.value)}
                    className="input-default"
                    placeholder="Şifreyi tekrar girin"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setResetStep(1)}
                    className="flex-1 py-3 rounded-xl border-2 border-[#E5E7EB] text-[#073B4C] font-bold hover:bg-gray-50 transition-colors"
                  >
                    Geri
                  </button>
                  <button
                    data-testid="reset-submit-btn"
                    onClick={handleResetSubmit}
                    disabled={resetLoading}
                    className="btn-primary flex-1 disabled:opacity-50"
                  >
                    {resetLoading ? "Güncelleniyor..." : "Şifreyi Güncelle"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
